/**
 * Boot loader (roc-151q.6): fetch the deployment's runtime config, then — and
 * only then — load the real app. Everything in bootstrap.ts (and its imports:
 * auth-config, msalService, apiService, ...) may read runtimeConfig() at
 * module scope precisely because it is loaded via this dynamic import, after
 * the config is installed. Do not add app imports to this file.
 */
import { loadRuntimeConfig } from './runtime-config';
// Stale-build detection (roc-s9fj). Dependency-free by design, so it does not
// violate the no-app-imports rule above; imported here rather than from
// bootstrap so the vite:preloadError listener is installed before the very
// first dynamic import — including the import('./bootstrap') below.
import './stale-build';
// The boot-failure shell (roc-odn8). Dependency-free for the same reason.
import { renderBootFailure, type BootStage } from './boot-failure';
// Recovery for module requests lost in flight (roc-mez2.3.3). Dependency-free too.
import { tryBootReload } from './boot-retry';
// Bounds for the import below (roc-pvnv). Dependency-free too.
import { withTimeout } from './lib/bounded';

/**
 * How long the app graph may take to arrive before the boot is declared hung.
 *
 * Wide, because this covers bootstrap.ts and everything it pulls in over
 * whatever link the operator has — and a false trip here costs a reload of a
 * boot that was merely slow. It exists for the case that has no cost at all
 * today: a request that never settles, where main.ts's catch is unreachable,
 * nothing is logged, and the tab sits on index.html's placeholder forever.
 */
const APP_LOAD_TIMEOUT_MS = 60_000;

function fail(stage: BootStage, error: unknown): void {
  console.error(stage === 'config' ? 'Runtime config load failed:' : 'App load failed:', error);
  const el = document.getElementById('app');
  if (el) void renderBootFailure(el, error, stage);
}

/**
 * The two stages are caught separately because they fail for different reasons
 * and the operator needs telling which one happened. They used to share one
 * catch, so a lost module request was reported as "the deployment configuration
 * could not be loaded" — a sentence about a file that had already been fetched
 * successfully. That message is what sent roc-mez2.3.3 chasing the dev server
 * for a session.
 */
async function boot(): Promise<void> {
  try {
    await loadRuntimeConfig();
  } catch (error) {
    // No config, no app: MSAL, the API client and the map keys all need it.
    // Render the failure where a blank page would otherwise be (same loud-
    // failure stance as runtime-config.ts).
    return fail('config', error);
  }

  try {
    await withTimeout(import('./bootstrap'), APP_LOAD_TIMEOUT_MS, 'app load');
    // The reload budget is NOT cleared here. bootstrap's modules arriving is not
    // the app being up — the router still has a lazy route to fetch, and that
    // fetch can be lost the same way (router/index.ts). Clearing on this line
    // handed a permanently unfetchable route a fresh budget on every reload.
    // router.isReady() resolving is the first moment something is on screen, so
    // it owns the clear now.
  } catch (error) {
    // A reload, not a retry: the module map caches failed fetches, so this
    // document can never refetch what it lost (see boot-retry.ts).
    if (await tryBootReload(error)) return;
    return fail('app', error);
  }
}

void boot();
